import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { 
  Users, User, Settings, LogOut, Save, ArrowLeft 
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import ProfilePhotoUploader from '@/components/ProfilePhotoUploader';
import { isValidCaliforniaZipCode } from '@/utils/zipCodeValidation';
import api from '@/lib/api';

const CITIES = [
  "Los Angeles", "San Diego", "San Francisco", "Bay Area", "Sacramento",
  "Orange County", "San Jose", "Santa Barbara", "Palm Springs", "Other California"
];

const KIDS_AGES = [
  { value: "0-1", label: "0-1 years" },
  { value: "1-3", label: "1-3 years" },
  { value: "3-6", label: "3-6 years" },
  { value: "6-12", label: "6-12 years" },
  { value: "12+", label: "12+ years" }
];

const VALUES = [
  "Entrepreneurship", "Wellness", "Faith", "Culture", 
  "Adventure", "Career", "Creativity", "Social Impact"
];

const ACTIVITIES = [
  "Coffee/brunch", "Outdoor activities", "Fitness classes",
  "Wine nights", "Cultural events", "Kids playdates"
];

export default function ProfileEditPage() {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [zipCodeError, setZipCodeError] = useState('');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    city: '',
    zip_code: '',
    kids_ages: [],
    values: [],
    activities: [],
    bio: '',
    profile_photo_url: ''
  });

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        city: user.city || '',
        zip_code: user.zip_code || '',
        kids_ages: user.kids_ages || [],
        values: user.values || [],
        activities: user.activities || [],
        bio: user.bio || '',
        profile_photo_url: user.profile_photo_url || ''
      });
    }
  }, [user]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Validate zip code on change
    if (field === 'zip_code') {
      if (value && value.length === 5) {
        if (!isValidCaliforniaZipCode(value)) {
          setZipCodeError('Please enter a valid California zip code');
        } else {
          setZipCodeError('');
        }
      } else {
        setZipCodeError('');
      }
    }
  };

  const handleZipCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 5);
    handleChange('zip_code', value);
  };

  const toggleArrayValue = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate zip code before submit
    if (formData.zip_code && !isValidCaliforniaZipCode(formData.zip_code)) {
      toast.error('Please enter a valid California zip code');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await api.put('/me', formData);
      updateUser(response.data);
      toast.success('Profile updated!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream font-body">
      {/* Header */}
      <header className="bg-white border-b border-border-subtle sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-text-mid hover:text-navy">
            <ArrowLeft size={20} />
            <span>Back to Dashboard</span>
          </Link>
          <button onClick={() => { logout(); navigate('/'); }} className="text-text-mid hover:text-red-500">
            <LogOut size={20} />
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="font-display text-3xl text-navy mb-8">Edit Profile</h1>

          {user?.can_manage_events && !user?.is_admin && (
            <div className="bg-teal/10 border border-teal/30 rounded-2xl p-4 mb-6 flex items-center justify-between gap-3" data-testid="profile-coadmin-banner">
              <div>
                <div className="font-semibold text-navy">You&apos;re an Events Co-Admin</div>
                <p className="text-sm text-text-mid">Switch to your Co-Admin Dashboard to create and manage events.</p>
              </div>
              <Link to="/coadmin/dashboard">
                <Button size="sm" className="bg-teal hover:bg-teal-deep text-white rounded-full" data-testid="profile-coadmin-link">
                  Open Dashboard
                </Button>
              </Link>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8" data-testid="profile-edit-form">
            {/* Photo */}
            <div className="flex justify-center">
              <ProfilePhotoUploader
                currentPhotoUrl={formData.profile_photo_url}
                firstName={formData.first_name}
                lastName={formData.last_name}
                size={128}
                onUploaded={(url) => {
                  setFormData(prev => ({ ...prev, profile_photo_url: url }));
                  updateUser({ profile_photo_url: url });
                }}
                onDeleted={() => {
                  setFormData(prev => ({ ...prev, profile_photo_url: '' }));
                  updateUser({ profile_photo_url: null });
                }}
                testIdPrefix="profile-edit-photo"
              />
            </div>

            {/* Basic Info */}
            <div className="bg-white rounded-2xl p-6 shadow-card space-y-4">
              <h2 className="font-semibold text-navy">Basic Information</h2>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-navy">First Name</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => handleChange('first_name', e.target.value)}
                    className="rounded-xl border-seafoam"
                    data-testid="profile-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-navy">Last Name</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => handleChange('last_name', e.target.value)}
                    className="rounded-xl border-seafoam"
                    data-testid="profile-last-name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-navy">City</Label>
                <Select value={formData.city} onValueChange={(v) => handleChange('city', v)}>
                  <SelectTrigger className="rounded-xl border-seafoam" data-testid="profile-city">
                    <SelectValue placeholder="Select city" />
                  </SelectTrigger>
                  <SelectContent>
                    {CITIES.map(city => (
                      <SelectItem key={city} value={city}>{city}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-navy">Zip Code</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={5}
                  value={formData.zip_code}
                  onChange={handleZipCodeChange}
                  placeholder="90210"
                  className={`rounded-xl border-seafoam ${zipCodeError ? 'border-red-400' : ''}`}
                  data-testid="profile-zip-code"
                />
                {zipCodeError && (
                  <p className="text-red-500 text-sm">{zipCodeError}</p>
                )}
                <p className="text-text-light text-xs">California zip codes only</p>
              </div>

              <div className="space-y-2">
                <Label className="text-navy">Bio</Label>
                <Textarea
                  value={formData.bio}
                  onChange={(e) => handleChange('bio', e.target.value)}
                  placeholder="Tell other moms about yourself..."
                  rows={4}
                  className="rounded-xl border-seafoam resize-none"
                  data-testid="profile-bio"
                />
              </div>
            </div>

            {/* Kids Ages */}
            <div className="bg-white rounded-2xl p-6 shadow-card space-y-4">
              <h2 className="font-semibold text-navy">Kids Ages</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {KIDS_AGES.map(age => (
                  <label 
                    key={age.value}
                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                      formData.kids_ages.includes(age.value)
                        ? 'border-teal bg-teal/5'
                        : 'border-seafoam hover:border-teal/50'
                    }`}
                  >
                    <Checkbox
                      checked={formData.kids_ages.includes(age.value)}
                      onCheckedChange={() => toggleArrayValue('kids_ages', age.value)}
                    />
                    <span className="text-sm text-navy">{age.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Values */}
            <div className="bg-white rounded-2xl p-6 shadow-card space-y-4">
              <h2 className="font-semibold text-navy">Values (up to 3)</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {VALUES.map(value => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => toggleArrayValue('values', value)}
                    disabled={formData.values.length >= 3 && !formData.values.includes(value)}
                    className={`p-3 rounded-xl text-sm font-medium transition-all ${
                      formData.values.includes(value)
                        ? 'bg-teal text-white'
                        : 'bg-cream text-navy hover:bg-seafoam/50'
                    } ${formData.values.length >= 3 && !formData.values.includes(value) ? 'opacity-50' : ''}`}
                  >
                    {value}
                  </button>
                ))}
              </div>
            </div>

            {/* Activities */}
            <div className="bg-white rounded-2xl p-6 shadow-card space-y-4">
              <h2 className="font-semibold text-navy">Preferred Activities</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {ACTIVITIES.map(activity => (
                  <button
                    key={activity}
                    type="button"
                    onClick={() => toggleArrayValue('activities', activity)}
                    className={`p-3 rounded-xl text-sm font-medium transition-all ${
                      formData.activities.includes(activity)
                        ? 'bg-teal text-white'
                        : 'bg-cream text-navy hover:bg-seafoam/50'
                    }`}
                  >
                    {activity}
                  </button>
                ))}
              </div>
            </div>

            {/* Save Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-teal hover:bg-teal-deep text-white rounded-full h-12"
              data-testid="profile-save-btn"
            >
              {loading ? 'Saving...' : (
                <>
                  <Save size={18} className="mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </form>
        </motion.div>
      </main>
    </div>
  );
}
